<template>
    <div class="ath-table-row f fh fj-sb fa-c pr dw-10"
        :class="[isEven ? 'ath-row-even' : 'ath-row-odd', isClickable ? 'c-p' : '']"
        @click="$emit('click')">
        <div v-for="column in uiColumns"
            class="ath-table-cell sp-l-s f fa-c"
            :style="{
                'width': column.width_rem + 'rem',
                'height': column.height_rem ? column.height_rem + 'rem' : '100%',
                'left': column.left_position + 'rem',
                'right': column.right_position + 'rem'
            }"
            :class="[
                column.locked ? 'ath-lock-column' : '',
                column.left_locked ? 'ath-left-lock-column' : '',
                column.right_locked ? 'ath-right-lock-column' : '',
                column.text_classes,
                column.align === 'right' ? 'fj-r sp-r-s' : '',
                column.align === 'center' ? 'fj-c' : ''
            ]"
            :key="data.uid + column.key"
            @click="$emit('clickColumn', column.key)">
            <AthVisualEntityTitle v-if="column.type === 'entity-title'"
                class="dh-10 ath-table-title-cell"
                v-bind="data[column.key + '_rendered']"
                :icon="icon.length > 0"
                :iconCname="icon" />
            <AthVisualBadge v-else-if="column.type === 'badge'"
                class="dh-10 fa-c"
                v-bind="data[column.key + '_rendered']" />
            <AthVisualNumber v-else-if="column.type === 'number'"
                class="dh-10"
                v-bind="data[column.key + '_rendered']" />
            <AthVisualNumberBasic v-else-if="column.type === 'number_basic'"
                class="dh-10"
                v-bind="data[column.key + '_rendered']" />
            <AthVisualText v-else-if="column.type === 'text'"
                class="dh-10"
                v-bind="data[column.key + '_rendered']" />
            <AthVisualBreakdown v-else-if="column.type === 'breakdown'"
                class="dh-10"
                v-bind="data[column.key + '_rendered']" />
            <ButtonPill v-else-if="column.type === 'dropdown' && hasDropdownItems"
                class="el--s-m"
                icon="ellipsis"
                v-bind="column.ui_options"
                @click.native.stop="onShowDropdown($event, column)" />
            <AthButtonIcon v-else-if="column.type === 'button'"
                class="el--s-m"
                v-bind="column.ui_options"
                @click.native.stop="onButtonClicked(column.key)" />
            <AthFormCheckbox v-else-if="column.type === 'checkbox'"
                class="dh-10 fj-c"
                :size="data[column.key + '_rendered'].size || 's'"
                :value="data[column.key + '_rendered'].value"
                :disabled="data[column.key + '_rendered'].disabled"
                :intermediate="data[column.key + '_rendered'].intermediate"
                @input="onCheckboxChange(column.key, $event)"
                @click.native.stop />
        </div>
        <AthButtonIcon v-if="allowRedirect"
            class="ath-redirect-button"
            icon="chevron-right"
            @click.prevent.stop="$emit('redirect')" />
    </div>
</template>
<script>
import { guuid, isNeArray } from '@aretheyhappy/utils';
import AthButtonIcon from '../../atoms/ButtonIcon.vue';
import AthVisualEntityTitle from '../../atoms/visuals/EntityTitle.vue';
import AthVisualBadge from '../../atoms/visuals/Badge.vue';
import AthVisualNumber from '../../atoms/visuals/Number.vue';
import AthVisualNumberBasic from '../../atoms/visuals/NumberBasic.vue';
import AthVisualText from '../../atoms/visuals/Text.vue';
import AthVisualBreakdown from '../../atoms/visuals/Breakdown.vue';
import AthFormCheckbox from '../../atoms/forms/FormCheckbox.vue';
import List from '../list/List.vue';
import ButtonPill from '../../atoms/ButtonPill.vue';

export default {
    name: 'mead-table-row',
    props: {
        /**
         * The row's data record, including its rendered cell values (per column, as
         * `<key>_rendered`), config (clickable, dropdown_options), and optional children.
         */
        data: {type: Object, default: () => ({})},
        /**
         * The column definitions describing which cell type to render, its width/positioning,
         * alignment, and lock state for each column of this row.
         */
        columns: {type: Array, default: () => []},
        /**
         * Whether this row's children rows are currently expanded; controls whether the
         * expand/collapse chevron icon points down or right.
         */
        expanded: {type: Boolean, default: () => false},
        /**
         * The row's position in the table, used to alternate even/odd row styling and included
         * in emitted events (onDropdownItemClicked, onButtonClicked, onCheckboxChange).
         */
        index: {type: Number, default: 0},
        /**
         * Whether to show a trailing chevron button that emits a "redirect" event when clicked.
         */
        allowRedirect: {type: Boolean, default: () => false},
    },
    components: {
        AthVisualEntityTitle,
        AthVisualBadge,
        AthVisualNumber,
        AthVisualNumberBasic,
        AthVisualText,
        AthVisualBreakdown,
        AthFormCheckbox,
        AthButtonIcon,
        ButtonPill,
    },
    data: function () {
        return {
            uid: guuid(),
        };
    },
    methods: {
        onShowDropdown: function (event, column) {
            const listItems = []
            for (const item of this.data.config.dropdown_options) {
                listItems.push(new MeadCommon.ListItem(MeadCommon.I18N.go(item.lbl), () => {
                    this.onDropdownItemClicked(item);
                }, item.icon).setQATag(`${column.key}-${item.option}`));
            }

            MeadCommon.PopoverService.openPopover(new MeadCommon.PopoverConfig()
                .setComponent(List)
                .setEvent(event)
                .setShowArrow(false)
                .setProps({
                    items: listItems
                }))
        },
        onDropdownItemClicked: function (el) {
            this.$emit('onDropdownItemClicked', Object.assign({
                index: this.index,
            }, el));
            MeadCommon.PopoverService.close();
        },
        onButtonClicked: function (columnKey) {
            this.$emit('onButtonClicked', {
                data: this.data,
                column: columnKey,
                index: this.index,
            });
        },
        onCheckboxChange: function (columnKey, value) {
            this.$emit('onCheckboxChange', {
                data: this.data,
                column: columnKey,
                value: value,
                index: this.index,
            });
        }
    },
    computed: {
        hasDropdownItems: function () {
            return this.data?.config?.dropdown_options.length > 0
        },
        hasChildren: function () {
            return isNeArray(this.data.children);
        },
        isEven: function () {
            return this.index % 2 === 0;
        },
        icon: function () {
            if (!this.hasChildren) return '';
            return this.expanded ? 'lnr-chevron-down' : 'lnr-chevron-right';
        },
        /**
         * Number of locked columns from the left side of the row
         */
        nrOfLockedLeftColumns: function () {
            let nr = 0;

            for (const header of this.columns) {
                if (!header.locked) break;

                nr++;
            }

            return nr;
        },
        /**
         * Number of locked columns from the right side of the row
         */
        nrOfLockedRightColumns: function () {
            let nr = 0;

            const columns = [...this.columns].reverse();
            for (const header of columns) {
                if (!header.locked) break;

                nr++;
            }

            return nr;
        },
        /**
         * Columns that are locked in the beginning of the row
         */
        leftLockedColumns: function () {
            const list = [];

            if (this.nrOfLockedLeftColumns === 0) return list;

            for (let i = 0; i < this.nrOfLockedLeftColumns; i++) {
                let leftPosition = 0;

                for (const el of list) {
                    leftPosition += el.width_rem;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {left_position: leftPosition, left_locked: true}));
            }

            return list;
        },
        /**
         * Build a new set of columns that are not fixed
         */
        computedColumns: function () {
            const list = [];
            for (let i = this.nrOfLockedLeftColumns; i < this.columns.length - this.nrOfLockedRightColumns; i++) {
                let leftPosition = 0;
                let locked = this.columns[i].locked;

                for (const el of list) {
                    leftPosition += el.width_rem;

                    // If header is locked but not all previous headers are set this one to also not be locked
                    if (this.columns[i].locked && !el.locked) locked = false;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {locked, left_position: leftPosition}));
            }

            return list;
        },
        /**
         * Columns that are locked at the end of the row
         */
        rightLockedColumns: function () {
            const list = [];

            if (this.nrOfLockedRightColumns === 0) return list;

            for (let i = this.columns.length - 1; i >= this.columns.length - this.nrOfLockedRightColumns; i--) {
                let rightPosition = 0;

                for (const el of list) {
                    rightPosition += el.width_rem;
                }

                // Add the base header into the list
                list.push(Object.assign({}, this.columns[i], {right_position: rightPosition, right_locked: true}));
            }

            return list.reverse();
        },
        uiColumns: function () {
            return [...this.leftLockedColumns, ...this.computedColumns, ...this.rightLockedColumns];
        },
        /**
         * If the table row should be clickable
         */
        isClickable: function () {
            return this.data?.config?.clickable;
        },
    },
};
</script>
<style lang="scss">
@import '../../../../sass/_variables.scss';

.ath-table-title-cell {
    .ath-visual-entity-title-el {
        font-family: $ff-be;
        font-size: $fs-s;
    }
}
</style>
<style lang="scss" scoped>
.ath-table-row {
    height: 5rem;
}

.ath-table-title-cell {
    text-transform: capitalize;
}

.ath-redirect-button {
    position: absolute;
    right: 0rem;
    visibility: hidden;
}

.ath-table-row:hover {
    .ath-redirect-button {
        visibility: visible;
    }
}
</style>